import React, { useMemo, useState } from "react";
import { Button, Alert } from "@mui/material";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ReplayIcon from "@mui/icons-material/Replay";
import { useNavigate } from "react-router-dom";
import { AnswerReviewDialog } from "@/components/common";
import FinalScoreDialog, {
  ScoreBar,
  TotalCircle,
  outlinedSx,
  containedSx,
} from "@/pages/user/ExamSession/FinalScoreDialog";
import { buildPublicMockHeaderChips } from "./partMeta";

const PersistenceBanner = ({ result, isAuthenticated }) => {
  const navigate = useNavigate();
  if (result?.persisted) {
    return (
      <Alert
        severity="success"
        sx={{ mb: 3, borderRadius: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => navigate("/mock-tests/history")}
          >
            View history
          </Button>
        }
      >
        Result saved to your history.
      </Alert>
    );
  }
  if (isAuthenticated) return null;
  return (
    <Alert
      severity="info"
      sx={{ mb: 3, borderRadius: 2 }}
      action={
        <Button color="inherit" size="small" onClick={() => navigate("/login")}>
          Log in
        </Button>
      }
    >
      Log in to save your results and track your progress.
    </Alert>
  );
};

/**
 * Part-mode body: same Listening / Reading bars + Total circle as the regular
 * full test, but driven by `sectionSubtotal` counts. Sections without any
 * selected parts are hidden so single-part practice (e.g. only Part 5) renders
 * just one bar plus the circle.
 */
const PartScoreBody = ({ result }) => {
  const subtotal = result?.sectionSubtotal || {};
  const listening = subtotal.listening;
  const reading = subtotal.reading;

  const totals = useMemo(() => {
    const correct = (result?.partScores || []).reduce(
      (sum, p) => sum + (p.correct || 0),
      0
    );
    const total = (result?.partScores || []).reduce(
      (sum, p) => sum + (p.total || 0),
      0
    );
    const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
    return { correct, total, percentage };
  }, [result]);

  return (
    <>
      {listening && (
        <ScoreBar
          label="Listening"
          value={listening.correct}
          max={listening.total || 1}
          displayValue={`${listening.correct}/${listening.total}`}
        />
      )}
      {reading && (
        <ScoreBar
          label="Reading"
          value={reading.correct}
          max={reading.total || 1}
          displayValue={`${reading.correct}/${reading.total}`}
        />
      )}
      <TotalCircle
        value={totals.correct}
        max={totals.total || 1}
        displayValue={`${totals.percentage}%`}
      />
    </>
  );
};

/**
 * Result popup for a completed public mock test. Reuses the shared
 * `FinalScoreDialog` (Listening / Reading / Total) and just supplies the
 * public-mode header banner, actions, and a part-mode body when needed.
 */
const Result = ({
  result,
  detailedGroups = [],
  examTitle,
  isAuthenticated,
  onRetake,
}) => {
  const navigate = useNavigate();
  const isPart = result?.mode === "part";
  const [reviewOpen, setReviewOpen] = useState(false);
  const hasReview = Array.isArray(detailedGroups) && detailedGroups.length > 0;

  const reviewHeaderChips = buildPublicMockHeaderChips({
    isPart,
    score: result?.score ?? 0,
    listeningScore: result?.listeningScore ?? 0,
    readingScore: result?.readingScore ?? 0,
    parts: result?.selectedParts || result?.parts || [],
  });

  const goBack = () => navigate("/mock-tests");

  return (
    <>
      <FinalScoreDialog
        openExamResultDialog
        handleCloseExamResultDialog={goBack}
        onClose={goBack}
        finishExamData={{
          data: {
            listeningScore: result?.listeningScore,
            readingScore: result?.readingScore,
            score: result?.score,
          },
        }}
        subtitle={
          isPart
            ? `Practice complete${examTitle ? ` — ${examTitle}` : ""}`
            : "Congratulations on completing your exam!"
        }
        headerSlot={
          <PersistenceBanner result={result} isAuthenticated={isAuthenticated} />
        }
        bodySlot={isPart ? <PartScoreBody result={result} /> : null}
        actions={({ onCancel }) => (
          <>
            <Button variant="outlined" onClick={onCancel} sx={outlinedSx}>
              Back to tests
            </Button>
            {onRetake && (
              <Button
                variant="outlined"
                startIcon={<ReplayIcon />}
                onClick={onRetake}
                sx={outlinedSx}
              >
                Retake
              </Button>
            )}
            {hasReview && (
              <Button
                variant="contained"
                startIcon={<FactCheckIcon />}
                onClick={() => setReviewOpen(true)}
                sx={containedSx}
              >
                View Detailed Results
              </Button>
            )}
          </>
        )}
      />

      <AnswerReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        title={`${examTitle || "Mock test"} — Detailed Results`}
        subtitle={isPart ? "Practice by part" : "Full test"}
        headerChips={reviewHeaderChips}
        detailedGroups={detailedGroups}
        emptyMessage="No answer details available for this attempt."
      />
    </>
  );
};

export default Result;
